/**
 * 준비물 흐름 - 3택1 제시 · 고른 것 · 라운드 시작의 소비
 *
 * 근거 문서: 로드맵 확정안 4절 3번(로그라이크의 3택1) · 3.2절(준비물은 넷뿐이다) · M9 규칙 3 ·
 *           `game/economy/kitDraft.ts` 머리말(가진 것 중에서 · 무한 재추첨 금지) ·
 *           `game/roundTuning.ts` 머리말(단계 → 준비물 → URL)
 *
 * ## 고른 것은 **무대가 설 때** 소비되고, 그 무대가 서 있는 동안 얹혀 있다
 *
 * 안내 화면에서 고른 순간에 빼면 뒤로 나갔다 온 사람이 준비물을 잃는다. 반대로 라운드가 끝난 뒤에 빼면
 * 판 도중에 새로고침한 사람이 공짜로 한 판을 돈다. 그래서 소비 시점은 `startLevel`의 한 줄이고
 * (`consume`), 그 자리는 무대를 세우기 **직전**이다 - 소비와 튜닝 적용이 같은 호출 구간에 있어야
 * "썼는데 안 얹힌" 판이 생길 수 없다.
 *
 * ## 준비물 하나가 사는 단위는 "한 판"이 아니라 **"이 단계 방문 한 번"**이다
 *
 * 세션의 튜닝은 무대가 설 때 한 번 굳는다(`game/session.ts`가 `deps.tuning`을 상수로 붙든다 - HUD·리듀서·
 * 결과 화면이 같은 값을 봐야 하므로 판마다 갈아 끼울 수 있는 값이 아니다). 그래서 결과 화면의
 * "다른 변화로 다시"와 "한 판 더"는 무대를 다시 세우지 않고, 챙긴 준비물의 보너스는 **단계 선택으로
 * 나갈 때까지** 남는다. 사물함에서는 한 개만 빠졌는데 그 단계에 머무는 모든 판에 얹힌다는 뜻이다.
 *
 * 그게 지금의 계약이다(`kit.pickHint`가 화면에서 그대로 말한다). 판마다 다시 빼려면 재도전이 무대를
 * 통째로 다시 세워야 하는데, 그건 방을 다시 굽는 일이라 재도전의 값이 준비물 하나보다 비싸진다.
 * **반대 방향은 여전히 막혀 있다** - 안 썼는데 얹힌 판은 없다(소비가 실패하면 그 판은 빈손이다).
 *
 * ## 시도 번호는 그 단계를 몇 번 했는가다
 *
 * 제시 셋의 시드는 `(계정, 단계, 시도)`인데(`kitDraftSeed`), 안내 화면에는 아직 시도 번호가 없다
 * (그 번호는 세션이 라운드 안에서 센다). 그래서 **저장된 판 수**(`LevelRecord.plays`)를 그 자리에 쓴다.
 * 안내 화면이 열려 있는 동안 움직이지 않는 값이라 다시 열어도 같은 셋이 나오고(무한 재추첨 금지),
 * 한 판을 돌고 오면 하나 늘어 다음 셋이 달라진다. `?attempt=`를 쓰지 않는 이유는 그것이 **변화**의
 * 번호이지 판 수가 아니기 때문이다 - 같은 방을 다시 도는 도구가 준비물 제시까지 흔들 이유가 없다.
 *
 * ## 규칙은 여기 없다
 *
 * 무엇을 제시할지(`draftKits`) · 몇 개 가졌는지(`inventory.kits`) · 튜닝에 어떻게 얹히는지(`withKit`)는
 * 전부 순수 모듈의 것이다. 이 파일이 쥔 상태는 **지금 고른 것 하나**뿐이다.
 */

import { draftKits } from '../game/economy/kitDraft'
import type { Inventory } from '../game/economy/inventory'
import { getKit, type KitDef, type KitId } from '../game/economy/kits'
import type { KitPickView } from '../ui/kitPick'

export type KitFlowDeps = Readonly<{
  /** 계정 시드. 제시 셋이 계정마다 다른 근거다(`kitDraftSeed`). */
  accountSeed: () => string
  inventory: () => Inventory
  /** 그 단계를 지금까지 몇 판 했는가(`progress.LevelRecord.plays`). 없으면 0. */
  playsOf: (levelId: number) => number
  /** 준비물 하나를 쓴다(`accountEconomy.useKit`). 없으면 거짓이다. */
  useKit: (id: KitId) => boolean
}>

export type KitFlow = Readonly<{
  /**
   * 안내 화면이 그릴 값. **부를 때마다 제시 셋을 다시 계산한다** - 사물함이 바뀌었을 수 있고
   * (봉투를 열고 왔다), 계산은 정렬 한 번이라 싸다. 시드가 같으니 결과도 같다.
   */
  viewFor: (levelId: number) => KitPickView
  /** 카드를 눌렀다. 같은 것을 다시 누르면 `null`(빈손)이 온다. */
  pick: (id: KitId | null) => void
  /** 지금 고른 것. 아무것도 안 골랐으면 `null`. */
  picked: () => KitDef | null
  /**
   * 무대가 선다 - 고른 것을 사물함에서 빼고 그 정의를 돌려준다.
   * 뺄 수 없으면(사이에 다 써 버렸다) `null`이고, 그 판은 빈손으로 돈다.
   * **부른 뒤 고른 것은 비워진다** - 다음 안내 화면이 지난 판의 선택을 물려받으면 공짜로 한 번 더 쓴다.
   * 그 무대 위에서 도는 재도전·"한 판 더"에는 다시 부르지 않는다(머리말의 "이 단계 방문 한 번").
   */
  consume: () => KitDef | null
  /** 화면을 떠난다(뒤로 · 단계를 바꿨다). 고른 것만 지운다 - 사물함은 건드리지 않는다. */
  reset: () => void
}>

export function createKitFlow(deps: KitFlowDeps): KitFlow {
  /** 지금 고른 것. 이 모듈이 쥔 상태의 전부다. */
  let picked: KitId | null = null

  function options(levelId: number): readonly KitId[] {
    return draftKits(deps.inventory(), deps.accountSeed(), levelId, deps.playsOf(levelId))
  }

  return {
    viewFor(levelId: number): KitPickView {
      const drafted = options(levelId)
      /*
       * 제시 셋에 없는 것을 고른 채로 둘 수 없다 - 다른 단계에서 고른 것이 그대로 남으면 화면에는
       * 아무 카드도 눌린 것이 없는데 라운드에는 준비물이 얹힌다. 그 상태는 화면 어디에도 증상이 없다.
       */
      if (picked !== null && !drafted.includes(picked)) picked = null

      const inventory = deps.inventory()
      return {
        options: drafted.map((id) => ({ id, count: inventory.kits[id] })),
        picked,
      }
    },

    pick(id: KitId | null): void {
      picked = id
    },

    picked: () => (picked === null ? null : getKit(picked)),

    consume(): KitDef | null {
      if (picked === null) return null
      const id = picked
      picked = null
      // 실제로 뺄 수 있을 때만 얹는다. 거짓이면 그 판은 빈손이다 - 없는 것을 쓴 판이 생기면 보유 수가 음수가 된다.
      return deps.useKit(id) ? getKit(id) : null
    },

    reset(): void {
      picked = null
    },
  }
}
